import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  Alert,
  Tooltip,
  Snackbar,
  SelectChangeEvent,
  Badge,
} from '@mui/material';
import {
  AccountCircle,
  Dashboard,
  People,
  Business,
  LocalOffer,
  Analytics,
  Settings,
  Logout,
  Add,
  Edit,
  Delete,
  Visibility,
  PhoneAndroid,
  PersonAdd,
  Check,
  Close,
  Cancel,
  Pending,
  CheckCircle,
  Email,
  Phone,
  Schedule,
  Flag,
  FlagOutlined,
  Map as MapIcon,
  History,
  PlayArrow,
  Search,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { useLoadScript, GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import AddVenueFromPlacesDialog from '../components/super-admin/AddVenueFromPlacesDialog';
import { DuplicateVenueError, findDuplicateVenue, PlaceVenueDraft } from '../utils/venueFromGooglePlace';
import { isVenueSearchStillLoading, matchesFuzzyQuery, shouldLoadRemainingVenuesForSearch } from '../utils/venueFuzzySearch';
import { httpsCallable as fbCallable } from 'firebase/functions';
import { collection, getDocs, query, orderBy, limit, doc, updateDoc, deleteDoc, where, addDoc, onSnapshot, QuerySnapshot, DocumentData, Timestamp, startAfter } from 'firebase/firestore';
import { db, functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';
import SuperAdminAnalytics from '../components/analytics/SuperAdminAnalytics';
import BestTimeAnalytics from '../components/analytics/BesttimeAnalytics';
import SettingsComponent from '../components/shared/Settings';
import ScrapedOffersReview from '../components/ScrapedOffersReview';
import {AnalyticsDashboard, VenueAnalyticsEvent, Stats, VenueAdmin, Venue, Offer, AdminAction, MobileUserStats, FirebaseFunctionResponse, VenueApprovalRequest} from '../types/SuperAdminTypes';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import JamIcon from '../assets/JamIcon.png';
import { isNamelessVenue, venueUsageFromDoc } from '../utils/unnamedVenue';

const GOOGLE_MAP_LIBRARIES: ('places')[] = ['places'];
const VENUES_PAGE_SIZE = 500;

function mapVenuePageDoc(venueDoc: { id: string; data: () => any }): Venue {
  const d = venueDoc.data();
  return {
    id: venueDoc.id,
    name: d.name,
    businessName: d.businessName || d.name || 'Unknown',
    tradingName: d.tradingName,
    email: d.email || '',
    phone: d.phone || '',
    address: d.address || d.location?.formattedAddress || '',
    location: d.location,
    status: d.status || d.portalStatus || 'pending',
    portalStatus: d.portalStatus,
    ownerId: d.ownerId || '',
    ownership: d.ownership,
    usage: d.usage,
    subscription: d.subscription,
    analytics: d.analytics,
    profile: d.profile,
    createdAt: d.createdAt,
    updatedAt: d.updatedAt,
    createdBy: d.createdBy || '',
    totalViews: d.totalViews || 0,
    totalSearches: d.totalSearches || 0,
    totalFavorites: d.totalFavorites || 0,
    isVenuePortalEnabled: d.isVenuePortalEnabled,
  } as any as Venue;
}

function appendUniqueVenues(existing: Venue[], incoming: Venue[]): Venue[] {
  if (incoming.length === 0) return existing;
  const seen = new Set(existing.map((venue) => venue.id));
  const unique = incoming.filter((venue) => venue.id && !seen.has(venue.id));
  return unique.length === 0 ? existing : [...existing, ...unique];
}

const SuperAdminDashboard: React.FC = () => {
  const { signOut, superAdminData, currentUser } = useAuth();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [stats, setStats] = useState<Stats>({
    totalVenueAdmins: 0,
    totalVenues: 0,
    totalOffers: 0,
    activeSubscriptions: 0,
    monthlyRevenue: 0,
    mobileUsers: {
      authenticatedUsers: 0,
      totalUsers: 0,
      guestUsers: 0,
      loading: false,
      error: null,
    },
  });
  const [venueAdmins, setVenueAdmins] = useState<VenueAdmin[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [venuesLastDoc, setVenuesLastDoc] = useState<any>(null);
  const [venuesHasMore, setVenuesHasMore] = useState(false);
  const [venuesLoadingMore, setVenuesLoadingMore] = useState(false);
  const [venueSearchLoading, setVenueSearchLoading] = useState(false);
  const [venueSearchLoadFailed, setVenueSearchLoadFailed] = useState(false);
  const venueSearchPagingRef = useRef(false);
  const venueSearchFailedQueryRef = useRef<string | null>(null);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [subscriptionPlans, setSubscriptionPlans] = useState<{ planId: string; priceMonthly: number }[]>([]);

  // User Management state
  const [userManagementTab, setUserManagementTab] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [actionDialog, setActionDialog] = useState<AdminAction | null>(null);
  const [editDialog, setEditDialog] = useState<VenueAdmin | null>(null);
  const [selectedAdmins, setSelectedAdmins] = useState<string[]>([]);

  // Edit form state
  const [editName, setEditName] = useState<string>('');
  const [editEmail, setEditEmail] = useState<string>('');
  const [editPhone, setEditPhone] = useState<string>('');
  const [editCompany, setEditCompany] = useState<string>('');
  const [editVenueLimit, setEditVenueLimit] = useState<number>(1);
  const [editStatus, setEditStatus] = useState<'pending' | 'active' | 'suspended' | 'rejected'>('pending');

  // Venue Approval state
  const [venueApprovalRequests, setVenueApprovalRequests] = useState<VenueApprovalRequest[]>([]);
  const [venueApprovalTab, setVenueApprovalTab] = useState<number>(0);
  const [selectedRequest, setSelectedRequest] = useState<VenueApprovalRequest | null>(null);
  const [approvalDialog, setApprovalDialog] = useState<{
    type: 'approve' | 'reject';
    request: VenueApprovalRequest;
  } | null>(null);
  const [reviewNotes, setReviewNotes] = useState<string>('');
  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
    // Load growth trend data from daily_stats
    const loadGrowthData = async () => {
      try {
        const snap = await getDocs(query(
          collection(db, 'daily_stats'),
          orderBy('lastUpdated', 'desc'),
          limit(30)
        ));
        const data = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .reverse()
          .map((d: any) => ({
            date: d.id,
            searches: d.total_searches ?? d.total_searchs ?? 0,
            favorites: d.total_favorites || 0,
            venues: Array.isArray(d.unique_venues_searched) ? d.unique_venues_searched.length : 0,
          }));
        setGrowthData(data);
      } catch (e) {
        console.warn('Growth data unavailable:', e);
      }
    };
    loadGrowthData();
    // Load portal API key for map
    const getKey = httpsCallable(functions, 'getPortalApiKey');
    getKey({}).then((r: any) => setMapApiKey(r.data?.googlePlacesApiKey || '')).catch(() => {});
    // Load flagged venues from Firestore
    getDocs(query(collection(db, 'bdm_flags'))).then(snap => {
      setFlaggedVenueIds(new Set(snap.docs.map(d => d.id)));
    }).catch(() => {});

    // Live badge count for pending scraped offers — limit to avoid loading all docs
    const scrapedUnsub = onSnapshot(
      query(collection(db, 'scraped_offers'), where('status', '==', 'pending_review'), limit(200)),
      snap => setScrapedPendingCount(snap.size),
      () => {}
    );
    return () => scrapedUnsub();
  }, []);

  //Analytics stats
  const [analyticsData, setAnalyticsData] = useState<AnalyticsDashboard | null>(null);
const [recentEvents, setRecentEvents] = useState<VenueAnalyticsEvent[]>([]);

//Venue Management
const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
const [viewDialogOpen, setViewDialogOpen] = useState(false);
const [editDialogOpen, setEditDialogOpen] = useState(false);
const [deleteVenueDialog, setDeleteVenueDialog] = useState<Venue | null>(null);
const [venueStatusFilter, setVenueStatusFilter] = useState<string>('all');
const [venueStateFilter, setVenueStateFilter] = useState<string>('all');
const [venueSearchQuery, setVenueSearchQuery] = useState<string>('');
const [hideIncompleteVenues, setHideIncompleteVenues] = useState<boolean>(true);
const [venueEditForm, setVenueEditForm] = useState({
  businessName: '',
  email: '',
  status: '',
  ownerId: '',
  ownerName: '',
  website: '',
  bookingUrl: '',
});
const [venueEditSaving, setVenueEditSaving] = useState(false);
  const [venueSubTab, setVenueSubTab] = useState<'managed' | 'prospects'>('managed');
  const [scrapingVenueId, setScrapingVenueId] = useState<string | null>(null);
  const [scrapeResult, setScrapeResult] = useState<{ venueId: string; message: string } | null>(null);
  const [growthData, setGrowthData] = useState<any[]>([]);
  const [sessionDialog, setSessionDialog] = useState<{ admin: any; sessions: any[] } | null>(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [flaggedVenueIds, setFlaggedVenueIds] = useState<Set<string>>(new Set());
  const [mapApiKey, setMapApiKey] = useState<string>('');
  const [selectedMapVenue, setSelectedMapVenue] = useState<any>(null);
  const [showMap, setShowMap] = useState(false);
  const [scrapedPendingCount, setScrapedPendingCount] = useState<number>(0);
  const [addVenueDialogOpen, setAddVenueDialogOpen] = useState(false);
  const [addVenueSaving, setAddVenueSaving] = useState(false);

  const { isLoaded: mapsLoaded } = useLoadScript({
    googleMapsApiKey: mapApiKey,
    libraries: GOOGLE_MAP_LIBRARIES,
  });


  // Optimized dashboard loading with performance improvements

const loadDashboardData = async (): Promise<void> => {
  try {
    setLoading(true);
    console.time('Total Dashboard Load');

    // OPTIMIZATION 1: Load critical data first (parallel)
    console.time('Critical Data Load');
    const [venueAdminsSnapshot, venuesSnapshot, offersSnapshot] = await Promise.all([
      getDocs(query(
        collection(db, 'venue_admins'),
        orderBy('createdAt', 'desc'),
        limit(10) // latest 10 only for now
      )),
      getDocs(query(
        collection(db, 'venues'),
        limit(VENUES_PAGE_SIZE)
      )),
      getDocs(query(
        collection(db, 'offers'),
        orderBy('createdAt', 'desc'),
        limit(10) 
      ))
    ]);

    // Load ALL venue admins for revenue — limit fields fetched
    const allVenueAdminsSnapshot = await getDocs(query(
      collection(db, 'venue_admins'),
      limit(200) // cap for memory
    ));
    const allVenueAdminsData = allVenueAdminsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as VenueAdmin));

    // Load subscription plans for accurate pricing
    const plansSnapshot = await getDocs(query(
      collection(db, 'admin_subscription_plans'),
      where('isActive', '==', true)
    ));
    const plansData = plansSnapshot.docs.map(doc => ({
      planId: doc.data().planId,
      priceMonthly: doc.data().priceMonthly,
    }));
    setSubscriptionPlans(plansData);

    // Load active offers count accurately
    const activeOffersSnapshot = await getDocs(query(
      collection(db, 'offers'),
      where('isActive', '==', true),
      where('status', '==', 'active')
    ));
    console.timeEnd('Critical Data Load');

    // OPTIMIZATION 2: Process data immediately after loading
    const venueAdminsData = venueAdminsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as VenueAdmin));

    const venuesData = venuesSnapshot.docs.map(doc => {
      const d = doc.data();
      // Only keep fields we display — drop heavy fields like photoUrl, openingHours, profile subtree
      return {
        id: doc.id,
        name: d.name, businessName: d.businessName, tradingName: d.tradingName,
        displayName: d.displayName,
        address: d.address || d.location?.formattedAddress,
        email: d.email, status: d.status, portalStatus: d.portalStatus,
        ownerId: d.ownerId, createdBy: d.createdBy, ownership: d.ownership,
        isVenuePortalEnabled: d.isVenuePortalEnabled,
        usage: venueUsageFromDoc(d),
        totalSearches: d.totalSearches, totalViews: d.totalViews, totalFavorites: d.totalFavorites,
        latitude: d.latitude, longitude: d.longitude,
        location: d.location,
        subscription: d.subscription,
        createdAt: d.createdAt, updatedAt: d.updatedAt,
        types: d.types,
        profile: { website: d.profile?.website, booking: d.profile?.booking },
        website: d.website,
      } as any as Venue;
    });

    const offersData = offersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Offer));

    // OPTIMIZATION 3: Update UI with critical data first
    setVenueAdmins(venueAdminsData);
    setVenues(venuesData);
    setVenuesLastDoc(venuesSnapshot.docs[venuesSnapshot.docs.length - 1] || null);
    setVenuesHasMore(venuesSnapshot.docs.length >= VENUES_PAGE_SIZE);
    setOffers(offersData);

    // Calculate basic stats immediately
    const basicStats = {
      totalVenueAdmins: venueAdminsSnapshot.size,
      totalVenues: venuesSnapshot.size,
      totalOffers: activeOffersSnapshot.size,
      activeSubscriptions: allVenueAdminsData.filter(
        admin => admin.subscription?.status === 'active'
      ).length,
      monthlyRevenue: allVenueAdminsData
        .filter(admin => admin.subscription?.status === 'active')
        .reduce((total, admin) => {
          const planPrice = getPlanPrice(admin.subscription?.planId);
          return total + planPrice;
        }, 0),
      mobileUsers: {
        authenticatedUsers: 0,
        totalUsers: 0,
        guestUsers: 0,
        loading: true,
        error: null,
      }
    };

    setStats(basicStats);
    setLoading(false); 

    // OPTIMIZATION 5: Load non-critical data in background
    console.time('Background Data Load');
    const backgroundPromises = [];

    // Load venue approvals (less critical)
    backgroundPromises.push(
      getDocs(query(
        collection(db, 'venue_approval_requests'),
        orderBy('submittedAt', 'desc'),
        limit(50) // LIMIT: Don't load all approval requests
      )).then(venueApprovalSnapshot => {
        const venueApprovalData = venueApprovalSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as VenueApprovalRequest));
        setVenueApprovalRequests(venueApprovalData);
      })
    );

    // Load analytics — derive from venues already in memory, no extra Firestore query needed
    const venueBreakdown = venuesData
      .map(venue => {
        const totalViews     = venue.totalViews     || 0;
        const totalSearches  = venue.totalSearches  || 0;
        const totalFavorites = venue.totalFavorites || 0;
        return {
          venueId:       venue.id,
          venueName:     isNamelessVenue(venue) ? 'Incomplete stub' : (venue.businessName || venue.name || 'Unnamed Venue'),
          totalViews,
          totalSearches,
          totalFavorites,
          totalActivity: totalViews + totalSearches + totalFavorites,
          recentActivity: []
        };
      })
      .filter(v => v.totalActivity > 0)
      .sort((a, b) => b.totalActivity - a.totalActivity)
      .slice(0, 20);

    const totalViews     = venuesData.reduce((s, v) => s + (v.totalViews     || 0), 0);
    const totalSearches  = venuesData.reduce((s, v) => s + (v.totalSearches  || 0), 0);
    const totalFavorites = venuesData.reduce((s, v) => s + (v.totalFavorites || 0), 0);

    setAnalyticsData({
      totalEvents:    totalViews + totalSearches + totalFavorites,
      totalViews,
      totalSearches,
      totalFavorites,
      venueBreakdown,
      recentActivity: []
    });
    setRecentEvents([]);

    // Load mobile user stats (slowest, least critical)
    backgroundPromises.push(
      loadMobileUserStats().then(mobileUserStats => {
        setStats(prevStats => ({
          ...prevStats,
          mobileUsers: mobileUserStats
        }));
      })
    );

    // Wait for background tasks
    await Promise.allSettled(backgroundPromises);
    console.timeEnd('Background Data Load');

  } catch (error: any) {
    console.error('Error loading dashboard data:', error);
    setLoading(false);
  } finally {
    console.timeEnd('Total Dashboard Load');
  }
};

// OPTIMIZATION 6: Extract mobile user stats to separate function
// Uses getUserStats Cloud Function which calls admin.auth().listUsers()
// for accurate counts regardless of whether users have Firestore docs.
const loadMobileUserStats = async (): Promise<MobileUserStats> => {
  let mobileUserStats: MobileUserStats = {
    authenticatedUsers: 0,
    totalUsers: 0,
    guestUsers: 0,
    loading: true,
    error: null,
  };

  try {
    const getUserStatsFn = httpsCallable(functions, 'getUserStats');
    const result = await getUserStatsFn({});
    const data = (result.data as any)?.data;

    if (!data) throw new Error('No data returned from getUserStats');

    // getUserStats returns:
    //   totalAuthUsers      — all Firebase Auth accounts (ground truth)
    //   usersWithProfiles   — users with a Firestore users/{uid} doc
    //   actualAdminUsers    — Auth users who are venue/super admins
    //   mobile.totalMobileUsers — totalAuthUsers minus admin accounts
    //   mobile.guestUsers   — mobile users without a profile doc

    const totalMobile        = data.mobile?.totalMobileUsers ?? Math.max(0, data.totalAuthUsers - data.actualAdminUsers);
    const usersWithProfiles  = data.usersWithProfiles ?? 0;
    const guestUsers         = data.mobile?.guestUsers ?? Math.max(0, totalMobile - usersWithProfiles);

    mobileUserStats.totalUsers         = totalMobile;
    mobileUserStats.authenticatedUsers  = usersWithProfiles;
    mobileUserStats.guestUsers          = guestUsers;
    mobileUserStats.loading             = false;

    console.log('📊 Mobile user stats from Auth:', {
      totalAuthUsers:    data.totalAuthUsers,
      adminUsers:        data.actualAdminUsers,
      totalMobile,
      usersWithProfiles,
      guestUsers,
    });

  } catch (error: any) {
    console.error('Error loading mobile user stats:', error);
    mobileUserStats.error = error.message;
    mobileUserStats.loading = false;
  }

  return mobileUserStats;
};

// OPTIMIZATION 7: Caching strategy (optional)
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const cache = new Map<string, { data: any; timestamp: number }>();

const getCachedData = (key: string) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// OPTIMIZATION 8: Lazy loading for tabs
const loadDashboardDataOptimized = async (activeTab: number = 0): Promise<void> => {
  // Only load data needed for the current tab
  switch (activeTab) {
    case 0: // Dashboard tab
      await loadEssentialData();
      break;
    case 1: // Venues tab
      await loadVenues();
      break;
    default:
      await loadEssentialData();
  }
};

const loadEssentialData = async () => {
  // Load only what's needed for dashboard overview
  const [venueAdminsSnapshot, basicStats] = await Promise.all([
    getDocs(query(collection(db, 'venue_admins'), limit(5))), // Just for recent admins widget
    getDocs(query(collection(db, 'venues'), limit(1))) // Just for count
  ]);
};

// OPTIMIZATION 9: Real-time updates only for critical data
const setupRealtimeListeners = () => {
  // Only listen to critical changes
  const unsubscribeVenueAdmins = onSnapshot(
    query(collection(db, 'venue_admins'), orderBy('createdAt', 'desc'), limit(5)),
    (snapshot) => {
      const recentAdmins = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as VenueAdmin));
      setVenueAdmins(recentAdmins);
    }
  );

  return () => {
    unsubscribeVenueAdmins();
  };
};

  const getPlanPrice = (planId?: string): number => {
    // Dynamically look up price from loaded subscription plans
    const plan = subscriptionPlans.find(p => p.planId === planId);
    return plan ? plan.priceMonthly / 100 : 0;
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(amount);
  };

  const getStatusColor = (status?: string): 'success' | 'info' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'suspended':
        return 'error';
      case 'pending':
      case 'stub':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'approved':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status?: string): React.ReactElement | null => {
    switch (status) {
      case 'active': return <CheckCircle />;
      case 'pending': return <Pending />;
      case 'suspended': return <Cancel />;
      case 'rejected': return <Cancel />;
      case 'approved': return <CheckCircle />;
      default: return null;
    }
  };
  // User Management Functions
  const handleActionClick = useCallback((type: AdminAction['type'], admin: VenueAdmin): void => {
    if (type === 'edit') {
      setEditName(admin.name);
      setEditEmail(admin.email);
      setEditPhone(admin.phone);
      setEditCompany(admin.company);
      setEditVenueLimit(admin.venueLimit);
      setEditStatus(admin.status);
      setEditDialog(admin);
    } else {
      setActionDialog({ type, admin });
    }
  }, []);

  const handleConfirmAction = useCallback(async (): Promise<void> => {
    if (!actionDialog) return;

    try {
      setLoading(true);
      const { type, admin } = actionDialog;
      
      switch (type) {
        case 'approve':
          console.log('Admin object:', admin);
  console.log('Admin.adminId:', admin.adminId);
  console.log('Admin.id:', admin.id);
  const approveFunction = httpsCallable(functions, 'approveVenueAdmin');
  await approveFunction({ adminId: admin.adminId }); 
  setSuccess(`Approved ${admin.name}'s account and started their subscription`);
  break;
          
        case 'reject':
          await updateDoc(doc(db, 'venue_admins', admin.id), {
            status: 'rejected',
            updatedAt: new Date()
          });
          setSuccess(`Rejected ${admin.name}'s account`);
          break;
          
        case 'suspend':
          await updateDoc(doc(db, 'venue_admins', admin.id), {
            status: 'suspended',
            updatedAt: new Date()
          });
          setSuccess(`Suspended ${admin.name}'s account`);
          break;
          
        case 'delete':
          // First clear ownership on all venues belonging to this admin
          const ownedVenuesSnapshot = await getDocs(query(
            collection(db, 'venues'),
            where('ownerId', '==', admin.adminId)
          ));
          if (!ownedVenuesSnapshot.empty) {
            await Promise.all(
              ownedVenuesSnapshot.docs.map(venueDoc =>
                updateDoc(venueDoc.ref, {
                  ownerId: null,
                  ownership: null,
                  status: 'unowned',
                  updatedAt: Timestamp.now(),
                })
              )
            );
          }
          // Then delete the admin doc
          await deleteDoc(doc(db, 'venue_admins', admin.id));
          setSuccess(`Deleted ${admin.name}'s account and released ${ownedVenuesSnapshot.size} venue(s)`);
          break;
      }
      
      setActionDialog(null);
      await loadDashboardData();
      
      
    } catch (error: any) {
      console.error('Error performing action:', error);
      setError(`Failed to ${actionDialog.type} account`);
    } finally {
      setLoading(false);
    }
  }, [actionDialog]);

  const handleSaveEdit = useCallback(async (): Promise<void> => {
    if (!editDialog) return;

    try {
      setLoading(true);
      await updateDoc(doc(db, 'venue_admins', editDialog.id), {
        name: editName,
        email: editEmail,
        phone: editPhone,
        company: editCompany,
        venueLimit: editVenueLimit,
        status: editStatus,
        updatedAt: new Date()
      });
      
      setSuccess(`Updated ${editName}'s account`);
      await loadDashboardData();
      setEditDialog(null);
      
    } catch (error: any) {
      console.error('Error updating admin:', error);
      setError('Failed to update account');
    } finally {
      setLoading(false);
    }
  }, [editDialog, editName, editEmail, editPhone, editCompany, editVenueLimit, editStatus]);

  const handleBulkAction = useCallback(async (action: 'approve' | 'reject' | 'suspend'): Promise<void> => {
  if (selectedAdmins.length === 0) return;

  try {
    setLoading(true);
    
    // For bulk approve, you should also use your Cloud Function instead of direct Firestore updates
    if (action === 'approve') {
      const approveFunction = httpsCallable(functions, 'approveVenueAdmin');
      const approvalPromises = selectedAdmins.map(adminId => 
        approveFunction({ adminId })
      );
      await Promise.all(approvalPromises);
    } else {
      const batchUpdates = selectedAdmins.map(adminId => 
        updateDoc(doc(db, 'venue_admins', adminId), {
          status: action,
          updatedAt: new Date()
        })
      );
      await Promise.all(batchUpdates);
    }
    
    setSuccess(`${action === 'approve' ? 'Approved' : action === 'reject' ? 'Rejected' : 'Suspended'} ${selectedAdmins.length} accounts`);
    setSelectedAdmins([]);
    await loadDashboardData();
    
    
  } catch (error: any) {
    console.error('Error performing bulk action:', error);
    setError('Failed to perform bulk action');
  } finally {
    setLoading(false);
  }
}, [selectedAdmins]);

  // Venue approval functions
  const openEditVenue = useCallback((venue: Venue) => {
    setVenueEditForm({
      businessName: venue.businessName || venue.name || '',
      email: venue.email || '',
      status: venue.status || 'pending',
      ownerId: venue.ownerId || '',
      ownerName: venue.ownership?.adminName || '',
      website: (venue as any).profile?.website || (venue as any).website || '',
      bookingUrl: (venue as any).profile?.booking?.url || '',
    });
    setSelectedVenue(venue);
    setEditDialogOpen(true);
  }, []);

  const handleSaveVenue = useCallback(async () => {
    if (!selectedVenue) return;
    try {
      setVenueEditSaving(true);
      const updateData: any = {
        businessName: venueEditForm.businessName.trim(),
        email: venueEditForm.email.trim(),
        status: venueEditForm.status,
        updatedAt: Timestamp.now(),
        'profile.website': venueEditForm.website.trim() || null,
        'profile.booking.url': venueEditForm.bookingUrl.trim() || null,
        website: venueEditForm.website.trim() || null,
      };

      // If owner has changed, update ownerId and ownership fields
      if (venueEditForm.ownerId !== selectedVenue.ownerId) {
        const selectedAdmin = venueAdmins.find(a => a.adminId === venueEditForm.ownerId);
        if (selectedAdmin) {
          updateData.ownerId = venueEditForm.ownerId;
          updateData.ownership = {
            adminId: selectedAdmin.adminId,
            adminEmail: selectedAdmin.email,
            adminName: selectedAdmin.name,
            createdBy: selectedAdmin.adminId,
          };
        } else if (!venueEditForm.ownerId) {
          updateData.ownerId = null;
          updateData.ownership = null;
        }
      }

      await updateDoc(doc(db, 'venues', selectedVenue.id), updateData);
      setSuccess(`${venueEditForm.businessName} updated successfully`);
      setEditDialogOpen(false);
      await loadDashboardData();
    } catch (err: any) {
      console.error('Error saving venue:', err);
      setError('Failed to save venue. Please try again.');
    } finally {
      setVenueEditSaving(false);
    }
  }, [selectedVenue, venueEditForm, venueAdmins]);

  const handleDeleteVenue = useCallback(async () => {
    if (!deleteVenueDialog) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'venues', deleteVenueDialog.id));
      setSuccess(`${deleteVenueDialog.businessName || deleteVenueDialog.name} deleted successfully`);
      setDeleteVenueDialog(null);
      await loadDashboardData();
    } catch (err: any) {
      console.error('Error deleting venue:', err);
      setError('Failed to delete venue. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [deleteVenueDialog]);

  const handleVenueApproval = useCallback(async (
    requestId: string, 
    action: 'approve' | 'reject', 
    notes?: string
  ): Promise<void> => {
    try {
      setLoading(true);
      const request = venueApprovalRequests.find(r => r.id === requestId);
      if (!request) throw new Error('Request not found');

      const now = new Date();
      
      if (action === 'approve') {
        if (request.requestType === 'create') {
          // Create new venue
          await createVenueFromRequest(request);
        } else if (request.requestType === 'claim') {
          // Transfer venue ownership
          await transferVenueOwnership(request);
        }
      }

      // Update the approval request
      await updateDoc(doc(db, 'venue_approval_requests', requestId), {
        status: action === 'approve' ? 'approved' : 'rejected',
        reviewedAt: now,
        reviewedBy: superAdminData?.name || 'Super Admin',
        reviewNotes: notes || ''
      });

      setSuccess(`Venue request ${action}d successfully`);
      await loadDashboardData();
      setApprovalDialog(null);
      setReviewNotes('');
      
    } catch (error: any) {
      console.error('Error processing venue approval:', error);
      setError(`Failed to ${action} venue request: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [venueApprovalRequests, superAdminData]);

  /**
   * Sanitise businessHours from the form before writing to Firestore.
   * The form stores closed days as null (Firestore round-trip) or as
   * { open, close, closed: true }. BestTime scheduler needs either
   * { open: 'HH:MM', close: 'HH:MM', closed: false } for open days
   * or { closed: true } for closed days — never null.
   */
  const sanitiseOpeningHours = (businessHours: any): Record<string, any> => {
    const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const result: Record<string, any> = {};
    for (const day of DAYS) {
      const raw = businessHours?.[day];
      if (!raw || raw === null) {
        // null means the form left it as default-closed
        result[day] = { closed: true };
      } else if (raw.closed === true) {
        result[day] = { closed: true };
      } else {
        result[day] = {
          open:   raw.open  || '09:00',
          close:  raw.close || '17:00',
          closed: false,
        };
      }
    }
    return result;
  };

  const createVenueFromRequest = async (request: VenueApprovalRequest): Promise<void> => {
    const now = new Date();
    const venueData = request.venueData;
    
    // Match Firebase structure
    const venueDocument = {
      // Just the basic fields
      name: request.businessName,
      tradingName: request.tradingName,
      businessName: request.businessName,
      email: venueData.email,
      phone: venueData.phone || null,
      abn: venueData.abn || null,
      maxCapacity: venueData.maxCapacity ? parseInt(venueData.maxCapacity, 10) : null,
      businessType: request.businessType,
      address: request.address,
      
      // Location data (will need to be geocoded)
      location: {
        formattedAddress: request.address,
        latitude: null,
        longitude: null,
        geohash: null
      },
      latitude: null,
      longitude: null,
      
      // Ownership
      ownerId: request.adminId,
      ownership: {
        adminId: request.adminId,
        adminEmail: request.adminEmail,
        adminName: request.adminName,
        createdBy: request.adminId
      },
      createdBy: request.adminId,
      
      // Profile
      profile: {
        description: venueData.description || null,
        website: venueData.website || null,
        priceRange: venueData.priceRange || null,
        cuisine: venueData.cuisine || [],
        amenities: venueData.amenities || [],
        openingHours: sanitiseOpeningHours(venueData.businessHours),
        logoUrl: null,
        coverImageUrl: null
      },
      
      // Analytics (initialized)
      analytics: {
        activeOffers: 0,
        monthlySearches: 0,
        monthlyViews: 0,
        totalOffers: 0,
        totalRedemptions: 0,
        lastActiveDate: null
      },
      
      // Usage
      usage: {
        offersLimit: 5,
        offersUsed: 0,
        lastResetDate: now
      },
      
      // Subscription (linked to admin's subscription)
      subscription: {
        planId: null,
        status: 'trialing',
        offersLimit: 5,
        offersUsed: 0,
        hasPortalAccess: true,
        trialEnd: new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000), // 14 days trial
        lastResetDate: now
      },
      
      // Verification status
      verificationStatus: {
        email: false,
        phone: false,
        abn: !!venueData.abn,
        address: false,
        documents: false
      },
      
      // Status and settings
      status: 'active',
      portalStatus: 'active',
      isVenuePortalEnabled: true,
      marketingConsent: venueData.marketingConsent || false,
      termsAccepted: venueData.termsAccepted || false,
      termsAcceptedAt: venueData.termsAccepted ? now : null,
      privacyPolicyAccepted: venueData.privacyPolicyAccepted || false,
      
      // Timestamps
      createdAt: now,
      updatedAt: now,
      lastUpdatedAt: now,
      lastLoginAt: null,
      
      // Counters
      totalFavorites: 0,
      totalSearches: 0,
      totalViews: 0
    };

    // Create the venue
    const venueRef = await addDoc(collection(db, 'venues'), venueDocument);
    
    // Update venue admin's venue count and venuesOwned
    const adminDoc = await getDocs(query(
      collection(db, 'venue_admins'),
      where('adminId', '==', request.adminId)
    ));
    
    if (!adminDoc.empty) {
      const adminRef = adminDoc.docs[0].ref;
      const adminData = adminDoc.docs[0].data();
      
      await updateDoc(adminRef, {
        venueCount: (adminData.venueCount || 0) + 1,
        venuesOwned: [...(adminData.venuesOwned || []), venueRef.id],
        updatedAt: now
      });
    }
  };

  const transferVenueOwnership = async (request: VenueApprovalRequest): Promise<void> => {
    if (!request.venueId) throw new Error('No venue ID for claim request');
    
    const now = new Date();
    
    // Update venue ownership + write profile/opening hours from claim form data
    const claimVenueData = request.venueData;
    await updateDoc(doc(db, 'venues', request.venueId), {
      ownerId: request.adminId,
      ownership: {
        adminId: request.adminId,
        adminEmail: request.adminEmail,
        adminName: request.adminName,
        createdBy: request.adminId
      },
      status: 'active',
      portalStatus: 'active',
      isVenuePortalEnabled: true,
      // Write profile fields submitted in the claim form
      'profile.openingHours': sanitiseOpeningHours(claimVenueData?.businessHours),
      'profile.description':  claimVenueData?.description  || null,
      'profile.website':      claimVenueData?.website      || null,
      'profile.priceRange':   claimVenueData?.priceRange   || null,
      'profile.cuisine':      claimVenueData?.cuisine      || [],
      'profile.amenities':    claimVenueData?.amenities    || [],
      updatedAt: now
    });
    
    // Update venue admin's venue count
    const adminDoc = await getDocs(query(
      collection(db, 'venue_admins'),
      where('adminId', '==', request.adminId)
    ));
    
    if (!adminDoc.empty) {
      const adminRef = adminDoc.docs[0].ref;
      const adminData = adminDoc.docs[0].data();
      
      await updateDoc(adminRef, {
        venueCount: (adminData.venueCount || 0) + 1,
        venuesOwned: [...(adminData.venuesOwned || []), request.venueId],
        updatedAt: now
      });
    }
  };
  // Dashboard Overview Tab
  const DashboardOverview = (): React.ReactElement => {
    const hour = new Date().getHours();
    const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
    const pendingCount = venueApprovalRequests.filter(r => r.status === 'pending').length;

    return (
    <Grid container spacing={3}>
      {/* Greeting Banner */}
      <Grid size={{ xs: 12 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {greeting}, {superAdminData?.name?.split(' ')[0]} 👋
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Here's what's happening across JamApp today
          </Typography>
        </Box>
        {pendingCount > 0 && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={() => setCurrentTab(3)}>
                Review
              </Button>
            }
            sx={{ mt: 1, borderRadius: 2 }}
          >
            <strong>{pendingCount} venue approval{pendingCount !== 1 ? 's' : ''}</strong> waiting for review
          </Alert>
        )}
      </Grid>

      {/* Metric Cards */}
      {[
        { label: 'Venue Admins', value: stats.totalVenueAdmins, icon: <People />, color: '#F24F2F', sub: `${stats.activeSubscriptions} active subscriptions` },
        { label: 'Venues in DB', value: stats.totalVenues, icon: <Business />, color: '#2196F3', sub: 'Tracked Firebase venues' },
        { label: 'Active Offers', value: stats.totalOffers, icon: <LocalOffer />, color: '#4CAF50', sub: 'Live across all venues' },
        { label: 'Monthly Revenue', value: formatCurrency(stats.monthlyRevenue), icon: <Analytics />, color: '#FF9800', sub: 'MRR estimate' },
      ].map((metric) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={metric.label}>
          <Card sx={{
            borderLeft: `4px solid ${metric.color}`,
            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' },
            transition: 'all 0.2s',
          }}>
            <CardContent sx={{ pb: '16px !important' }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                    {metric.label}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ my: 0.5, color: metric.color }}>
                    {metric.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{metric.sub}</Typography>
                </Box>
                <Box sx={{ color: metric.color, opacity: 0.3, fontSize: 40 }}>
                  {metric.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Growth Trends Chart */}
      <Grid size={{ xs: 12, md: 8 }}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>Growth Trends</Typography>
                <Typography variant="caption" color="text.secondary">Searches · Favourites · Unique Venues Searched — last 30 days</Typography>
              </Box>
            </Box>
            {growthData.length === 0 ? (
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No daily stats data yet — populate daily_stats collection to see trends
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={growthData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => v.slice(5)} /* MM-DD */
                    interval={6}
                  />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RechartsTooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 12 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="searches" stroke="#F24F2F" strokeWidth={2} dot={false} name="Searches" />
                  <Line type="monotone" dataKey="favorites" stroke="#2196F3" strokeWidth={2} dot={false} name="Favourites" />
                  <Line type="monotone" dataKey="venues" stroke="#4CAF50" strokeWidth={2} dot={false} name="Unique Venues Searched" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Mobile Users + Platform Summary */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>Mobile App Users</Typography>
            {[
              { label: 'Total Users', value: stats.mobileUsers.loading ? '...' : stats.mobileUsers.totalUsers, icon: <PhoneAndroid />, color: '#4CAF50', sub: 'Excl. admins' },
              { label: 'Registered', value: stats.mobileUsers.authenticatedUsers, icon: <PersonAdd />, color: '#2196F3', sub: 'With Firestore profiles' },
              { label: 'Guest Users', value: stats.mobileUsers.loading ? '...' : stats.mobileUsers.guestUsers, icon: <People />, color: '#9E9E9E', sub: 'No profile yet' },
            ].map((item) => (
              <Box key={item.label} display="flex" alignItems="center" justifyContent="space-between" py={1.5}
                sx={{ borderBottom: '1px solid #f5f5f5', '&:last-child': { borderBottom: 'none' } }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{ color: item.color, display: 'flex' }}>{item.icon}</Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{item.sub}</Typography>
                  </Box>
                </Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: item.color }}>{item.value}</Typography>
              </Box>
            ))}
            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">Registration rate</Typography>
                <Typography variant="caption" fontWeight={700} color="primary">
                  {stats.mobileUsers.totalUsers > 0
                    ? Math.round((stats.mobileUsers.authenticatedUsers / stats.mobileUsers.totalUsers) * 100)
                    : 0}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={stats.mobileUsers.totalUsers > 0
                  ? Math.round((stats.mobileUsers.authenticatedUsers / stats.mobileUsers.totalUsers) * 100)
                  : 0}
                sx={{ mt: 0.5, height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: '#F24F2F' } }}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Recent Venue Admins */}
<Grid size={{ xs: 12, lg: 6 }}>
  <Card>
    <CardContent>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Recent Venue Admins</Typography>
        <Button size="small" onClick={() => setCurrentTab(1)}>
          View All
        </Button>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Venues</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {venueAdmins.slice(0, 5).map((admin) => (
              <TableRow key={admin.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Avatar sx={{ mr: 1, width: 32, height: 32 }}>
                      {admin.name?.charAt(0)}
                    </Avatar>
                    <div>
                      <Typography variant="body2">{admin.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {admin.email}
                      </Typography>
                    </div>
                  </Box>
                </TableCell>
                <TableCell>{admin.company}</TableCell>
                <TableCell>
                  <Chip
                    label={admin.status || 'pending'}
                    color={getStatusColor(admin.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{admin.venueCount || 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </CardContent>
  </Card>
</Grid>

{/* Top 20 Venues - Separate Grid cell */}
<Grid size={{ xs: 12, lg: 6 }}>
  <Card>
    <CardContent>
      <Typography variant="h6" gutterBottom>
        Top 20 Venues - User Interaction
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>Venue Name</TableCell>
             {/* <TableCell align="right">Views</TableCell>
              <TableCell align="right">Searches</TableCell>
              <TableCell align="right">Favorites</TableCell>*/}
              <TableCell align="right">Total Interactions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analyticsData?.venueBreakdown.map((venue, index) => (
              <TableRow key={venue.venueId}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    #{index + 1}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ 
                    maxWidth: 320, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {venue.venueName}
                  </Typography>
                </TableCell>
               {/* <TableCell align="right">{venue.totalViews}</TableCell>
                <TableCell align="right">{venue.totalSearches}</TableCell>
                <TableCell align="right">{venue.totalFavorites}</TableCell> */}
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    {venue.totalActivity}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </CardContent>
  </Card>
</Grid>
    </Grid>
  );
  };
  const handleScrapeVenue = async (venue: any) => {
    if (scrapingVenueId) return; // prevent concurrent scrapes
    const venueName = venue.name || venue.businessName || 'Unknown';

    // Check venue has a website — scraper needs it
    const website = venue.profile?.website || venue.website || (venue as any).websiteUrl;
    if (!website) {
      setScrapeResult({ venueId: venue.id, message: `No website found for ${venueName} — scraper requires a website URL` });
      return;
    }

    setScrapingVenueId(venue.id);
    setScrapeResult(null);
    try {
      const triggerScrape = httpsCallable(functions, 'triggerVenueScraper');
      const result: any = await triggerScrape({ venueId: venue.id });
      setScrapeResult({ venueId: venue.id, message: result.data?.message || 'Scrape started successfully' });
    } catch (err: any) {
      setScrapeResult({ venueId: venue.id, message: `Error: ${err.message}` });
    } finally {
      setScrapingVenueId(null);
    }
  };

  const loadAdminSessions = async (admin: any) => {
    console.log('📋 loadAdminSessions called for:', admin?.adminId, admin?.name);
    setSessionDialog({ admin, sessions: [] });
    setSessionsLoading(true);
    try {
      const sessionsSnap = await getDocs(
        query(
          collection(db, 'venue_admin_sessions'),
          where('adminId', '==', admin.adminId),
          limit(50)
        )
      );
      const sessions = sessionsSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a: any, b: any) => (b.loginAt?.seconds || 0) - (a.loginAt?.seconds || 0));
      setSessionDialog({ admin, sessions });
    } catch (err: any) {
      console.error('Error loading sessions:', err);
      setSessionDialog({ admin, sessions: [] });
    } finally {
      setSessionsLoading(false);
    }
  };

  // Venue Admins Tab - will move this to seperate file post testing KW
  const VenueAdminsTab = (): React.ReactElement => {
    const adminStats = {
      pending: venueAdmins.filter(a => a.status === 'pending').length,
      active: venueAdmins.filter(a => a.status === 'active').length,
      suspended: venueAdmins.filter(a => a.status === 'suspended').length,
      rejected: venueAdmins.filter(a => a.status === 'rejected').length,
      total: venueAdmins.length,
    };

    const filteredAdmins = venueAdmins.filter(admin => {
      switch (userManagementTab) {
        case 0: return admin.status === 'pending';
        case 1: return admin.status === 'active';
        case 2: return admin.status === 'suspended';
        case 3: return admin.status === 'rejected';
        case 4: return true; // All
        default: return true;
      }
    });

    return (
      <Box>
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Pending color="warning" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" color="warning.main">
                  {adminStats.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircle color="success" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" color="success.main">
                  {adminStats.active}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Cancel color="error" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" color="error.main">
                  {adminStats.suspended}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Suspended
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Cancel color="error" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" color="error.main">
                  {adminStats.rejected}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rejected
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <People color="primary" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" color="primary.main">
                  {adminStats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Card>
          <CardContent>
            {/* Sub-tabs for status filtering */}
            <Tabs value={userManagementTab} onChange={(e, newValue) => setUserManagementTab(newValue)} sx={{ mb: 3 }}>
              <Tab label={`Pending (${adminStats.pending})`} />
              <Tab label={`Active (${adminStats.active})`} />
              <Tab label={`Suspended (${adminStats.suspended})`} />
              <Tab label={`Rejected (${adminStats.rejected})`} />
              <Tab label={`All (${adminStats.total})`} />
            </Tabs>

            {/* Bulk Actions */}
            {selectedAdmins.length > 0 && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'action.selected', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {selectedAdmins.length} admin(s) selected
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    size="small" 
                    variant="contained" 
                    color="success"
                    onClick={() => handleBulkAction('approve')}
                  >
                    Approve Selected
                  </Button>
                  <Button 
                    size="small" 
                    variant="contained" 
                    color="error"
                    onClick={() => handleBulkAction('reject')}
                  >
                    Reject Selected
                  </Button>
                  <Button 
                    size="small" 
                    variant="contained" 
                    color="warning"
                    onClick={() => handleBulkAction('suspend')}
                  >
                    Suspend Selected
                  </Button>
                  <Button 
                    size="small" 
                    variant="outlined"
                    onClick={() => setSelectedAdmins([])}
                  >
                    Clear Selection
                  </Button>
                </Box>
              </Box>
            )}
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAdmins(filteredAdmins.map(a => a.id));
                          } else {
                            setSelectedAdmins([]);
                          }
                        }}
                        checked={selectedAdmins.length === filteredAdmins.length && filteredAdmins.length > 0}
                      />
                    </TableCell>
                    <TableCell>Admin Details</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Venue Info</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Registration Date</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAdmins.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          checked={selectedAdmins.includes(admin.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAdmins([...selectedAdmins, admin.id]);
                            } else {
                              setSelectedAdmins(selectedAdmins.filter(id => id !== admin.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Box display="flex" alignItems="center">
                            <Avatar sx={{ mr: 1, width: 32, height: 32 }}>
                              {admin.name?.charAt(0)}
                            </Avatar>
                            <Typography variant="subtitle2">{admin.name}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Email sx={{ fontSize: 14 }} />
                            <Typography variant="body2" color="text.secondary">
                              {admin.email}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Phone sx={{ fontSize: 14 }} />
                            <Typography variant="body2" color="text.secondary">
                              {admin.phone}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{admin.company}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            ABN: {admin.abn}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {admin.businessType}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {admin.venueCount || 0}/{admin.venueLimit} venues
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Plan: {admin.subscription?.planId || 'N/A'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Offers: {admin.subscription?.totalOffersUsed || 0}/{admin.subscription?.totalOffersLimit || 0}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          {...(getStatusIcon(admin.status) && { icon: getStatusIcon(admin.status) as React.ReactElement })}
                          label={admin.status?.toUpperCase() || 'UNKNOWN'}
                          color={getStatusColor(admin.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {admin.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {admin.status === 'pending' && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleActionClick('approve', admin)}
                                >
                                  <Check />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleActionClick('reject', admin)}
                                >
                                  <Close />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {admin.status === 'active' && (
                            <Tooltip title="Suspend">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleActionClick('suspend', admin)}
                              >
                                <Cancel />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleActionClick('edit', admin)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Login History">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => {
                                console.log('🔑 History clicked for admin:', admin?.adminId, admin?.name);
                                loadAdminSessions(admin);
                              }}
                            >
                              <History />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleActionClick('delete', admin)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredAdmins.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No venue admins found for this category
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  const loadMoreVenues = async () => {
    if (!venuesLastDoc || venuesLoadingMore || venueSearchPagingRef.current) return;
    setVenuesLoadingMore(true);
    try {
      const nextQuery = query(
        collection(db, 'venues'),
        startAfter(venuesLastDoc),
        limit(VENUES_PAGE_SIZE)
      );
      const snapshot = await getDocs(nextQuery);
      const newVenues = snapshot.docs.map(doc => {
        const d = doc.data();
        return {
          id: doc.id,
          name: d.name, businessName: d.businessName, tradingName: d.tradingName,
          displayName: d.displayName,
          email: d.email || '', phone: d.phone || '',
          address: d.address || d.location?.formattedAddress || '',
          location: d.location, status: d.status || d.portalStatus || 'pending',
          portalStatus: d.portalStatus, ownerId: d.ownerId || '',
          ownership: d.ownership, usage: venueUsageFromDoc(d), subscription: d.subscription,
          analytics: d.analytics, profile: d.profile,
          createdAt: d.createdAt, updatedAt: d.updatedAt, createdBy: d.createdBy || '',
          totalViews: d.totalViews || 0, totalSearches: d.totalSearches || 0,
          totalFavorites: d.totalFavorites || 0,
          isVenuePortalEnabled: d.isVenuePortalEnabled,
        } as any as Venue;
      });
      setVenues(prev => [...prev, ...newVenues]);
      //const newVenues = snapshot.docs.map(mapVenuePageDoc);
      setVenues(prev => appendUniqueVenues(prev, newVenues));
      setVenuesLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setVenuesHasMore(snapshot.docs.length === VENUES_PAGE_SIZE);
      venueSearchFailedQueryRef.current = null;
      setVenueSearchLoadFailed(false);
    } catch (err) {
      console.error('Error loading more venues:', err);
    } finally {
      setVenuesLoadingMore(false);
    }
  };

  // Firestore cannot fuzzy-match, so a search has to page the rest of the
  // collection into memory. Keystrokes while a page-through is running hit the
  // ref and return; there is no cleanup so that in-flight load is not restarted.
  useEffect(() => {
    const trimmed = venueSearchQuery.trim();
    if (venueSearchPagingRef.current) return;
    if (!shouldLoadRemainingVenuesForSearch(
      trimmed,
      venuesHasMore,
      venuesLoadingMore || venueSearchLoading,
      venueSearchFailedQueryRef.current === trimmed,
    )) {
      return;
    }
    if (!venuesLastDoc) {
      setVenuesHasMore(false);
      return;
    }

    venueSearchPagingRef.current = true;
    setVenueSearchLoading(true);
    setVenueSearchLoadFailed(false);
    venueSearchFailedQueryRef.current = null;
    const startCursor = venuesLastDoc;

    void (async () => {
      let cursor: any = startCursor;
      let hasMore = true;
      const collected: Venue[] = [];
      try {
        while (hasMore && cursor) {
          const snapshot = await getDocs(query(
            collection(db, 'venues'),
            startAfter(cursor),
            limit(VENUES_PAGE_SIZE),
          ));
          collected.push(...snapshot.docs.map(mapVenuePageDoc));
          const next = snapshot.docs[snapshot.docs.length - 1] || null;
          if (!next || next.id === cursor.id) {
            hasMore = false;
            cursor = next;
            break;
          }
          cursor = next;
          hasMore = snapshot.docs.length === VENUES_PAGE_SIZE;
        }
        setVenues(prev => appendUniqueVenues(prev, collected));
        setVenuesLastDoc(cursor);
        setVenuesHasMore(hasMore);
      } catch (err) {
        console.error('Error loading venues for search:', err);
        venueSearchFailedQueryRef.current = trimmed;
        setVenueSearchLoadFailed(true);
        if (collected.length > 0) {
          setVenues(prev => appendUniqueVenues(prev, collected));
          setVenuesLastDoc(cursor);
        }
      } finally {
        venueSearchPagingRef.current = false;
        setVenueSearchLoading(false);
      }
    })();
  }, [venueSearchQuery, venuesHasMore, venuesLoadingMore, venuesLastDoc, venueSearchLoading]);

  const loadVenues = async () => {
  try {
    console.log('Loading venues...');
    const venuesQuery = query(collection(db, 'venues'), limit(2500));
    const venuesSnapshot = await getDocs(venuesQuery);
    
    console.log(`Found ${venuesSnapshot.docs.length} venues`);
    
    const venuesData = venuesSnapshot.docs.map(doc => {
      const data = doc.data();
      console.log('Raw venue data:', data); // Debug log
      
      return {
        id: doc.id,
        businessName: data.businessName || data.name || 'Unknown Business',
        name: data.name,
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || data.location?.formattedAddress || 'No address',
        location: data.location || {
          formattedAddress: data.address || '',
          latitude: null,
          longitude: null,
          geohash: null
        },
        status: data.status || data.portalStatus || 'pending',
        portalStatus: data.portalStatus,
        ownerId: data.ownerId || data.createdBy || '',
        ownership: data.ownership || {
          adminEmail: data.email || '',
          adminId: data.ownerId || data.createdBy || '',
          adminName: data.ownership?.adminName || 'Unknown Owner',
          createdBy: data.createdBy || data.ownerId || ''
        },
        usage: {
          offersUsed: data.usage?.offersUsed || data.subscription?.offersUsed || 0,
          offersLimit: data.usage?.offersLimit || data.subscription?.offersLimit || 5,
          lastResetDate: data.usage?.lastResetDate
        },
        subscription: data.subscription,
        analytics: data.analytics || {
          activeOffers: 0,
          monthlyViews: 0,
          monthlySearches: 0,
          totalOffers: 0,
          totalRedemptions: 0,
          lastActiveDate: null
        },
        profile: data.profile,
        abn: data.abn,
        businessType: data.businessType,
        tradingName: data.tradingName,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt || data.lastUpdatedAt,
        createdBy: data.createdBy || data.ownerId || '', // keep this in
        totalViews: data.totalViews || 0,
        totalSearches: data.totalSearches || 0,
        totalFavorites: data.totalFavorites || 0
      } as Venue;
    });
    
    console.log('Transformed venues:', venuesData);
    setVenues(venuesData);
  } catch (error) {
    console.error('Error loading venues:', error);
  }
};

const loadVenuesSimple = async () => {
  try {
    console.log('Loading venues...');
    const venuesQuery = query(collection(db, 'venues'), limit(2500));
    const venuesSnapshot = await getDocs(venuesQuery);
    
    console.log(`Found ${venuesSnapshot.docs.length} venues`);
    
    const venuesData = venuesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Venue[];
    
    console.log('Venues data:', venuesData);
    setVenues(venuesData);
  } catch (error) {
    console.error('Error loading venues:', error);
  }
};

  const handleCreateVenueFromPlace = async (draft: PlaceVenueDraft): Promise<void> => {
    setAddVenueSaving(true);
    try {
      try {
        const placeSnap = await getDocs(query(
          collection(db, 'venues'),
          where('location.placeId', '==', draft.placeId),
          limit(1)
        ));
        if (!placeSnap.empty) {
          const existing = placeSnap.docs[0];
          const data = existing.data();
          throw new DuplicateVenueError(
            existing.id,
            data.name || data.businessName || draft.name
          );
        }
      } catch (err) {
        if (err instanceof DuplicateVenueError) throw err;
        console.warn('placeId duplicate query skipped:', err);
      }

      const inMemoryMatch = findDuplicateVenue(venues, draft);
      if (inMemoryMatch) {
        throw new DuplicateVenueError(
          inMemoryMatch.id,
          inMemoryMatch.businessName || inMemoryMatch.name || draft.name
        );
      }

      const now = new Date();
      const createdBy = currentUser?.uid || superAdminData?.adminId || 'super_admin';

      await addDoc(collection(db, 'venues'), {
        name: draft.name,
        tradingName: draft.name,
        businessName: draft.name,
        email: null,
        phone: draft.phone,
        abn: null,
        maxCapacity: null,
        businessType: draft.businessType,
        address: draft.address,
        location: {
          formattedAddress: draft.address,
          latitude: draft.latitude,
          longitude: draft.longitude,
          geohash: null,
          placeId: draft.placeId,
        },
        latitude: draft.latitude,
        longitude: draft.longitude,
        ownerId: null,
        ownership: null,
        createdBy,
        profile: {
          description: null,
          website: draft.website,
          priceRange: null,
          cuisine: [],
          amenities: [],
          openingHours: sanitiseOpeningHours(draft.businessHours),
          logoUrl: null,
          coverImageUrl: null,
        },
        analytics: {
          activeOffers: 0,
          monthlySearches: 0,
          monthlyViews: 0,
          totalOffers: 0,
          totalRedemptions: 0,
          lastActiveDate: null,
        },
        usage: {
          offersLimit: 5,
          offersUsed: 0,
          lastResetDate: now,
        },
        subscription: {
          planId: null,
          status: 'trialing',
          offersLimit: 5,
          offersUsed: 0,
          hasPortalAccess: true,
          trialEnd: new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000),
          lastResetDate: now,
        },
        verificationStatus: {
          email: false,
          phone: !!draft.phone,
          abn: false,
          address: true,
          documents: false,
        },
        status: 'active',
        portalStatus: 'active',
        isVenuePortalEnabled: true,
        marketingConsent: false,
        termsAccepted: false,
        termsAcceptedAt: null,
        privacyPolicyAccepted: false,
        createdAt: now,
        updatedAt: now,
        lastUpdatedAt: now,
        lastLoginAt: null,
        totalFavorites: 0,
        totalSearches: 0,
        totalViews: 0,
      });

      setSuccess(`${draft.name} added as a managed venue`);
      setAddVenueDialogOpen(false);
      await loadVenues();
    } finally {
      setAddVenueSaving(false);
    }
  };

  // Venues Management Tab
  const VenuesTab = (): React.ReactElement => {
    // Parse Australian state from address string
    const parseState = (address: any): string => {
      if (!address) return 'Unknown';
      const str = typeof address === 'string' ? address : String(address);
      const statePattern = /\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/i;
      const match = str.match(statePattern);
      return match ? match[1].toUpperCase() : 'Unknown';
    };

    const availableStates = ['all', ...Array.from(new Set(
      venues.map(v => parseState(v.address || v.location?.formattedAddress || ''))
    )).filter(s => s !== 'Unknown').sort()];

    const statusCounts = {
      all: venues.length,
      active: venues.filter(v => v.status === 'active').length,
      verified: venues.filter(v => v.status === 'verified').length,
      pending: venues.filter(v => v.status === 'pending' && !isNamelessVenue(v)).length,
      stub: venues.filter(v => v.status === 'stub' || isNamelessVenue(v)).length,
      suspended: venues.filter(v => v.status === 'suspended').length,
      public: venues.filter(v => v.status === 'public').length,
    };

    const venueSearchFailed =
      venueSearchLoadFailed && venueSearchFailedQueryRef.current === venueSearchQuery.trim();
    const showVenueSearchLoading = isVenueSearchStillLoading(
      venueSearchQuery,
      venuesHasMore,
      venueSearchLoading,
      venueSearchFailed,
    );

    const filteredVenues = venues.filter(v => {
      const stateMatch = venueStateFilter === 'all' ||
        parseState(v.address || v.location?.formattedAddress || '') === venueStateFilter;
      const searchMatch = matchesFuzzyQuery(venueSearchQuery, [
        v.businessName,
        v.name,
        v.tradingName,
        v.address,
        v.location?.formattedAddress,
        v.email,
        v.ownership?.adminName,
      ]);
      return stateMatch && searchMatch;
    });
    const incompleteHiddenCount = hideIncompleteVenues
      ? venues.filter(v => isNamelessVenue(v)).length
      : 0;

    // Split venues
    const managedVenues = venues.filter(v => (v as any).isVenuePortalEnabled === true || v.status === 'active' || v.status === 'verified');
    const prospectVenues = [...venues]
      .filter(v => (v as any).isVenuePortalEnabled !== true && v.status !== 'active' && v.status !== 'verified')
      .filter(v => !(hideIncompleteVenues && isNamelessVenue(v)))
      .sort((a, b) => {
        const aScore = (a.totalSearches || 0) + (a.totalViews || 0) + (a.totalFavorites || 0);
        const bScore = (b.totalSearches || 0) + (b.totalViews || 0) + (b.totalFavorites || 0);
        return bScore - aScore;
      });

    const handleToggleFlag = async (venueId: string, venueName: string) => {
      const isFlagged = flaggedVenueIds.has(venueId);
      const ref = doc(db, 'bdm_flags', venueId);
      if (isFlagged) {
        await deleteDoc(ref).catch(() => {});
        setFlaggedVenueIds(prev => { const s = new Set(prev); s.delete(venueId); return s; });
      } else {
        await updateDoc(ref, { venueId, venueName, flaggedAt: Timestamp.now() })
          .catch(() => addDoc(collection(db, 'bdm_flags'), { venueId, venueName, flaggedAt: Timestamp.now() })
            .then(d => updateDoc(doc(db, 'bdm_flags', venueId), { venueId, venueName, flaggedAt: Timestamp.now() })
              .catch(() => {})));
        setFlaggedVenueIds(prev => new Set([...prev, venueId]));
      }
    };

    // Venues with coords for map
    const mappableVenues = venues.filter(v => ((v as any).latitude || (v as any).location?.latitude) && ((v as any).longitude || (v as any).location?.longitude));
    const mapCenter = { lat: -33.8688, lng: 151.2093 }; // Sydney default

    return (
  <>
    <Card>
      <CardContent>
        {(error || success) && (
          <Box mb={2}>
            {error && (
              <Alert severity="error" sx={{ mb: success ? 1 : 0 }} onClose={() => setError('')}>{error}</Alert>
            )}
            {success && (
              <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
            )}
          </Box>
        )}
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Venues Management</Typography>
            <Chip label={`Managed (${managedVenues.length})`} color={venueSubTab === 'managed' ? 'primary' : 'default'}
              onClick={() => setVenueSubTab('managed')} sx={{ cursor: 'pointer' }} />
            <Chip label={`Prospects (${prospectVenues.length})`} color={venueSubTab === 'prospects' ? 'warning' : 'default'}
              onClick={() => setVenueSubTab('prospects')} sx={{ cursor: 'pointer' }} />
            <Chip
              label="Hide incomplete"
              onClick={() => setHideIncompleteVenues(on => !on)}
              color={hideIncompleteVenues ? 'primary' : 'default'}
              variant={hideIncompleteVenues ? 'filled' : 'outlined'}
              size="small"
              sx={{ cursor: 'pointer' }}
            />
            {hideIncompleteVenues && (
              <Chip
                label={`${incompleteHiddenCount} incomplete hidden`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<MapIcon />} onClick={() => setShowMap(v => !v)}>
              {showMap ? 'Hide Map' : 'Show Map'}
            </Button>
            {venueSubTab === 'managed' && (
              <Button variant="contained" startIcon={<Add />} onClick={() => setAddVenueDialogOpen(true)}>
                Add Venue
              </Button>
            )}
          </Box>
        </Box>

        {/* Map */}
        {showMap && mapsLoaded && (
          <Box sx={{ width: '100%', height: 400, borderRadius: 2, overflow: 'hidden', mb: 3, border: '1px solid #e0e0e0' }}>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter}
              zoom={10}
              options={{ disableDefaultUI: false, zoomControl: true }}
            >
              {mappableVenues.map(venue => {
                const isManaged = (venue as any).isVenuePortalEnabled === true || venue.status === 'active' || venue.status === 'verified';
                const isFlagged = flaggedVenueIds.has(venue.id);
                return (
                  <Marker
                    key={venue.id}
                    position={{ lat: (venue as any).latitude || (venue as any).location?.latitude, lng: (venue as any).longitude || (venue as any).location?.longitude }}
                    onClick={() => setSelectedMapVenue(venue)}
                    icon={{
                      url: isManaged
                        ? 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
                        : isFlagged
                        ? 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png'
                        : 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                    }}
                  />
                );
              })}
              {selectedMapVenue && (
                <InfoWindow
                  position={{ lat: selectedMapVenue.latitude || selectedMapVenue.location?.latitude, lng: selectedMapVenue.longitude || selectedMapVenue.location?.longitude }}
                  onCloseClick={() => setSelectedMapVenue(null)}
                >
                  <Box sx={{ maxWidth: 220, p: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">{isNamelessVenue(selectedMapVenue) ? 'Incomplete stub' : (selectedMapVenue.name || selectedMapVenue.businessName)}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">{selectedMapVenue.address}</Typography>
                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                      <Typography variant="caption">🔍 {selectedMapVenue.totalSearches || 0} searches</Typography>
                      <Typography variant="caption">👁 {selectedMapVenue.totalViews || 0} views</Typography>
                      <Typography variant="caption">❤️ {selectedMapVenue.totalFavorites || 0} favs</Typography>
                    </Box>
                    <Chip label={selectedMapVenue.status} size="small" sx={{ mt: 0.5 }} />
                  </Box>
                </InfoWindow>
              )}
            </GoogleMap>
          </Box>
        )}
        {showMap && !mapsLoaded && (
          <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Map legend */}
        {showMap && (
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={0.5}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#F24F2F' }} /><Typography variant="caption">Managed</Typography></Box>
            <Box display="flex" alignItems="center" gap={0.5}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#2196F3' }} /><Typography variant="caption">Unmanaged</Typography></Box>
            <Box display="flex" alignItems="center" gap={0.5}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FFC107' }} /><Typography variant="caption">Flagged prospect</Typography></Box>
          </Box>
        )}

        {/* ── MANAGED TAB ── */}
        {venueSubTab === 'managed' && (
          <>
        <TextField
          size="small"
          fullWidth
          placeholder="Search venues by name or address"
          value={venueSearchQuery}
          onChange={(e) => setVenueSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          inputProps={{ 'aria-label': 'Search venues' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: venueSearchQuery ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear venue search"
                  onClick={() => setVenueSearchQuery('')}
                >
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />

        {showVenueSearchLoading && (
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Searching all venues…
            </Typography>
          </Box>
        )}
        {venueSearchFailed && venuesHasMore && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Could not load every venue. Results may be incomplete until you load more.
          </Alert>
        )}

        {/* Filters row */}
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2}>
          {/* State dropdown */}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>State</InputLabel>
            <Select
              value={venueStateFilter}
              label="State"
              onChange={(e) => setVenueStateFilter(e.target.value)}
            >
              {availableStates.map(state => (
                <MenuItem key={state} value={state}>
                  {state === 'all' ? 'All States' : state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status filter chips */}
          <Box display="flex" gap={1} flexWrap="wrap">
            {[
              { key: 'all', label: 'All' },
              { key: 'active', label: 'Active' },
              { key: 'verified', label: 'Verified' },
              { key: 'pending', label: 'Pending' },
              { key: 'stub', label: 'Stub' },
              { key: 'suspended', label: 'Suspended' },
              { key: 'public', label: 'Public' },
            ].map(({ key, label }) => (
              <Chip
                key={key}
                label={`${label} (${statusCounts[key as keyof typeof statusCounts] ?? 0})`}
                onClick={() => setVenueStatusFilter(key)}
                color={venueStatusFilter === key ? 'primary' : 'default'}
                variant={venueStatusFilter === key ? 'filled' : 'outlined'}
                size="small"
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>

          {/* Results count */}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            Showing {filteredVenues.length} of {venues.length}{venuesHasMore ? '+' : ''}
          </Typography>
        </Box>
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '25%' }}>Business</TableCell>
                <TableCell sx={{ width: '30%' }}>Location</TableCell>
                <TableCell sx={{ width: '15%' }}>Owner</TableCell>
                <TableCell sx={{ width: '10%' }}>Offers</TableCell>
                <TableCell sx={{ width: '10%' }}>Status</TableCell>
                <TableCell align="right" sx={{ width: '10%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredVenues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {showVenueSearchLoading
                        ? 'Searching all venues…'
                        : venueSearchQuery.trim()
                        ? 'No venues match your search'
                        : 'No venues match the selected filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredVenues.map((venue) => (
                <TableRow key={venue.id} hover>
                  <TableCell sx={{ maxWidth: 0 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {isNamelessVenue(venue) ? 'Incomplete stub' : (venue.businessName || venue.name || 'Unnamed Venue')}
                    </Typography>
                    {isNamelessVenue(venue) && (
                      <Chip label="Incomplete stub" color="warning" size="small" sx={{ mt: 0.5 }} />
                    )}
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {venue.email}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                      {venue.address}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {venue.ownership?.adminName || 'Unassigned'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" noWrap>
                        {venue.usage?.offersUsed || 0}/{venue.usage?.offersLimit || 5}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={((venue.usage?.offersUsed || 0) / (venue.usage?.offersLimit || 1)) * 100}
                        sx={{ width: 40, height: 4, borderRadius: 2 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={venue.status}
                      color={getStatusColor(venue.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={0} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => { setSelectedVenue(venue); setViewDialogOpen(true); }} title="View Details">
                        <Visibility fontSize="small" />
                      </IconButton>
                      <Tooltip title="Scrape offers from website">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleScrapeVenue(venue)}
                          disabled={scrapingVenueId === venue.id}
                        >
                          {scrapingVenueId === venue.id
                            ? <CircularProgress size={16} color="inherit" />
                            : <PlayArrow fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => openEditVenue(venue)} title="Edit Venue">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteVenueDialog(venue)} title="Delete Venue">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
          {/* Load More button */}
          {venuesHasMore && !showVenueSearchLoading && !venueSearchLoading && (
            <Box display="flex" justifyContent="center" alignItems="center" py={2} gap={2}>
              <Typography variant="caption" color="text.secondary">
                Showing {venues.length} of {venues.length}{venuesHasMore ? '+' : ''} venues
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={loadMoreVenues}
                disabled={venuesLoadingMore}
                startIcon={venuesLoadingMore ? <CircularProgress size={14} /> : null}
              >
                {venuesLoadingMore ? 'Loading...' : 'Load 500 more'}
              </Button>
            </Box>
          )}
          </>
        )}

        {/* ── PROSPECTS TAB ── */}
        {venueSubTab === 'prospects' && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Venue</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="center">Searches</TableCell>
                  <TableCell align="center">Views</TableCell>
                  <TableCell align="center">Favourites</TableCell>
                  <TableCell align="center">Total Activity</TableCell>
                  <TableCell align="center">BDM Flag</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prospectVenues.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No unmanaged venues found</Typography>
                    </TableCell>
                  </TableRow>
                ) : prospectVenues.map(venue => {
                  const totalActivity = (venue.totalSearches || 0) + (venue.totalViews || 0) + (venue.totalFavorites || 0);
                  const isFlagged = flaggedVenueIds.has(venue.id);
                  return (
                    <TableRow key={venue.id} hover sx={{ bgcolor: isFlagged ? 'rgba(255, 193, 7, 0.08)' : 'transparent' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {isNamelessVenue(venue) ? 'Incomplete stub' : (venue.name || venue.businessName || 'Unnamed Venue')}
                        </Typography>
                        <Chip label={venue.status || 'public'} size="small" sx={{ mt: 0.5 }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                          {venue.address || venue.location?.formattedAddress || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={500}>{venue.totalSearches || 0}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={500}>{venue.totalViews || 0}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={500}>{venue.totalFavorites || 0}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={totalActivity}
                          size="small"
                          color={totalActivity > 20 ? 'error' : totalActivity > 5 ? 'warning' : 'default'}
                          sx={{ fontWeight: 700, minWidth: 48 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title={isFlagged ? 'Remove BDM flag' : 'Flag for BDM follow-up'}>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleFlag(venue.id, venue.name || venue.businessName || '')}
                              color={isFlagged ? 'warning' : 'default'}
                            >
                              {isFlagged ? <Flag fontSize="small" /> : <FlagOutlined fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Scrape offers from website">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleScrapeVenue(venue)}
                              disabled={scrapingVenueId === venue.id}
                            >
                              {scrapingVenueId === venue.id
                                ? <CircularProgress size={16} color="inherit" />
                                : <PlayArrow fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>

    {/* View Venue Dialog */}
    <Dialog 
      open={viewDialogOpen} 
      onClose={() => setViewDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Venue Details</DialogTitle>
      <DialogContent>
        {selectedVenue && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">Business Name</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{selectedVenue.businessName}</Typography>
              
              <Typography variant="subtitle2">Email</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{selectedVenue.email}</Typography>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">Address</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedVenue.address}
              </Typography>
              
              <Typography variant="subtitle2">Owner</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedVenue.ownership?.adminName || 'Unknown Owner'}
              </Typography>
              
              <Typography variant="subtitle2">Status</Typography>
              <Chip label={selectedVenue.status} color={getStatusColor(selectedVenue.status)} size="small" />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Usage Statistics</Typography>
              <Typography variant="body2">
                Offers: {selectedVenue.usage?.offersUsed || 0} / {selectedVenue.usage?.offersLimit || 5}
              </Typography>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>

    {/* Edit Venue Dialog */}
    <Dialog 
      open={editDialogOpen} 
      onClose={() => setEditDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>Edit Venue — {selectedVenue?.businessName || selectedVenue?.name}</DialogTitle>
      <DialogContent>
        {selectedVenue && (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Business Name"
              value={venueEditForm.businessName}
              onChange={(e) => setVenueEditForm({ ...venueEditForm, businessName: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              value={venueEditForm.email}
              onChange={(e) => setVenueEditForm({ ...venueEditForm, email: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={venueEditForm.status}
                label="Status"
                onChange={(e) => setVenueEditForm({ ...venueEditForm, status: e.target.value })}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="verified">Verified</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Assign to Venue Admin</InputLabel>
              <Select
                value={venueEditForm.ownerId}
                label="Assign to Venue Admin"
                onChange={(e) => setVenueEditForm({ ...venueEditForm, ownerId: e.target.value })}
              >
                <MenuItem value="">— Unassigned —</MenuItem>
                {venueAdmins
                  .filter(a => a.status === 'active')
                  .map(admin => (
                    <MenuItem key={admin.adminId} value={admin.adminId}>
                      {admin.name} ({admin.email})
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
            {venueEditForm.ownerId !== selectedVenue.ownerId && (
              <Alert severity="warning">
                Reassigning this venue will update the owner in Firestore. Make sure the new admin has capacity in their plan.
              </Alert>
            )}
            <TextField
              fullWidth
              label="Website URL"
              placeholder="https://example.com"
              value={venueEditForm.website}
              onChange={(e) => setVenueEditForm({ ...venueEditForm, website: e.target.value })}
              helperText="Used by the scraper to find offers. Stored at profile.website"
            />
            <TextField
              fullWidth
              label="Booking URL"
              placeholder="https://example.com/book"
              value={venueEditForm.bookingUrl}
              onChange={(e) => setVenueEditForm({ ...venueEditForm, bookingUrl: e.target.value })}
              helperText="Booking/reservation link. Stored at profile.booking.url"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)} disabled={venueEditSaving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSaveVenue}
          disabled={venueEditSaving || !venueEditForm.businessName.trim()}
          startIcon={venueEditSaving ? <CircularProgress size={16} color="inherit" /> : <Edit />}
        >
          {venueEditSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete Venue Dialog */}
    <Dialog
      open={!!deleteVenueDialog}
      onClose={() => setDeleteVenueDialog(null)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Delete Venue</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          Are you sure you want to permanently delete <strong>{deleteVenueDialog?.businessName || deleteVenueDialog?.name}</strong>?
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          This will permanently delete the venue and cannot be undone. All associated data will be lost.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteVenueDialog(null)}>Cancel</Button>
        <Button variant="contained" color="error" onClick={handleDeleteVenue} startIcon={<Delete />}>
          Delete Permanently
        </Button>
      </DialogActions>
    </Dialog>

  </>
  );
  };

  // Venue Approvals Tab
  const VenueApprovalsTab = (): React.ReactElement => {
    const approvalStats = {
      pending: venueApprovalRequests.filter(r => r.status === 'pending').length,
      approved: venueApprovalRequests.filter(r => r.status === 'approved').length,
      rejected: venueApprovalRequests.filter(r => r.status === 'rejected').length,
      total: venueApprovalRequests.length,
    };

    const filteredRequests = venueApprovalRequests.filter(request => {
      switch (venueApprovalTab) {
        case 0: return request.status === 'pending';
        case 1: return request.status === 'approved';
        case 2: return request.status === 'rejected';
        case 3: return true; // All
        default: return true;
      }
    });

    return (
      <Box>
        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Pending color="warning" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" color="warning.main">
                  {approvalStats.pending}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Review
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircle color="success" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" color="success.main">
                  {approvalStats.approved}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Approved
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Cancel color="error" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" color="error.main">
                  {approvalStats.rejected}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Rejected
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Business color="primary" sx={{ fontSize: 32, mb: 1 }} />
                <Typography variant="h5" color="primary.main">
                  {approvalStats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            {/* Sub-tabs for status filtering */}
            <Tabs value={venueApprovalTab} onChange={(e, newValue) => setVenueApprovalTab(newValue)} sx={{ mb: 3 }}>
              <Tab label={`Pending (${approvalStats.pending})`} />
              <Tab label={`Approved (${approvalStats.approved})`} />
              <Tab label={`Rejected (${approvalStats.rejected})`} />
              <Tab label={`All (${approvalStats.total})`} />
            </Tabs>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Request Details</TableCell>
                    <TableCell>Admin Info</TableCell>
                    <TableCell>Business Info</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Submitted</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {request.businessName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.tradingName && request.tradingName !== request.businessName && 
                              `Trading as: ${request.tradingName}`
                            }
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.address}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">{request.adminName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.adminEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {request.businessType}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.venueData?.email}
                          </Typography>
                          {request.venueData?.phone && (
                            <Typography variant="body2" color="text.secondary">
                              {request.venueData.phone}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.requestType === 'create' ? 'New Venue' : 'Claim Venue'}
                          color={request.requestType === 'create' ? 'primary' : 'secondary'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.status?.toUpperCase() || 'PENDING'}
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {request.submittedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {request.status === 'pending' && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => setApprovalDialog({ type: 'approve', request })}
                                >
                                  <Check />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setApprovalDialog({ type: 'reject', request })}
                                >
                                  <Close />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setSelectedRequest(request)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredRequests.length === 0 && !loading && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No venue approval requests found for this category
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* App Bar — white, fixed, matches venue admin portal */}
      <AppBar position="fixed" elevation={0} sx={{
        bgcolor: '#ffffff',
        borderBottom: '1px solid #e0e0e0',
        color: '#000000',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}>
        <Toolbar sx={{ minHeight: '56px !important', px: 2 }}>
          {/* Logo */}
          <Box component="img" src={JamIcon} alt="JamApp" sx={{ width: 32, height: 32, objectFit: 'contain', mr: 1.5 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: '#000', fontSize: '1rem' }}>
            JamApp
            <Chip label="Super Admin" size="small" sx={{ ml: 1, bgcolor: '#F24F2F', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
          </Typography>
          <Typography variant="body2" sx={{ mr: 2, color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
            {superAdminData?.name}
          </Typography>
          <IconButton onClick={handleMenuClick} size="small">
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#F24F2F', fontSize: '0.875rem' }}>
              {superAdminData?.name?.charAt(0) || 'A'}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem onClick={() => { setCurrentTab(7); handleMenuClose(); }}>
              <Settings sx={{ mr: 1, fontSize: 18 }} /> Settings
            </MenuItem>
            <MenuItem onClick={signOut}>
              <Logout sx={{ mr: 1, fontSize: 18 }} /> Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>

        {/* Navigation Tabs */}
        <Box sx={{ borderTop: '1px solid #f0f0f0' }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 56,
              '& .MuiTab-root': { minHeight: 56, fontSize: '0.75rem', fontWeight: 600, textTransform: 'none', minWidth: 100 },
              '& .MuiTabs-indicator': { bgcolor: '#F24F2F' },
              '& .Mui-selected': { color: '#F24F2F !important' },
            }}
          >
            <Tab label="Dashboard" icon={<Dashboard sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Venue Admins" icon={<People sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Venues" icon={<Business sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label={
              <Badge badgeContent={venueApprovalRequests.filter(r => r.status === 'pending').length} color="error">
                Approvals
              </Badge>
            } icon={<Pending sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label={
              <Badge badgeContent={scrapedPendingCount > 0 ? scrapedPendingCount : undefined} color="error">
                Scraped Offers
              </Badge>
            } icon={<LocalOffer sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Analytics" icon={<Analytics sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Capacity" icon={<Schedule sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Settings" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
          </Tabs>
        </Box>
      </AppBar>

      {/* Spacer for fixed AppBar (toolbar + tabs height) */}
      <Box sx={{ height: '112px' }} />

      {/* Content */}
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        {currentTab === 0 && <DashboardOverview />}
        {currentTab === 1 && <VenueAdminsTab />}
        {/* Invoke as a function. VenuesTab is declared in this component, so
            <VenuesTab /> is a new component type every keystroke and the search
            input remounts and loses focus. */}
        {currentTab === 2 && VenuesTab()}
        {currentTab === 3 && <VenueApprovalsTab />}
        {currentTab === 4 && <ScrapedOffersReview />}
        {currentTab === 5 && <SuperAdminAnalytics/>}
        {currentTab === 6 && <BestTimeAnalytics/>}
        {currentTab === 7 && (
          <SettingsComponent
            userType="super_admin"
            userData={superAdminData}
            onUpdate={loadDashboardData}
          />
        )}
      </Container>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={!!actionDialog}
        onClose={() => setActionDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm {actionDialog?.type?.toUpperCase()} Action
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {actionDialog?.type} the account for{' '}
            <strong>{actionDialog?.admin?.name}</strong>?
          </Typography>
          {actionDialog?.type === 'delete' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will permanently delete the account and automatically release ownership of all their venues (venues will not be deleted, just unassigned). This cannot be undone.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button
            onClick={handleConfirmAction}
            color={actionDialog?.type === 'approve' ? 'success' : 'error'}
            variant="contained"
          >
            {actionDialog?.type?.toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={!!editDialog}
        onClose={() => setEditDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Venue Admin</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
            />
            <TextField
              label="Email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              fullWidth
            />
            <TextField
              label="Phone"
              value={editPhone}
              onChange={(e) => setEditPhone(e.target.value)}
              fullWidth
            />
            <TextField
              label="Company"
              value={editCompany}
              onChange={(e) => setEditCompany(e.target.value)}
              fullWidth
            />
            <TextField
              label="Venue Limit"
              type="number"
              value={editVenueLimit}
              onChange={(e) => setEditVenueLimit(Number(e.target.value))}
              fullWidth
              inputProps={{ min: 1, max: 100 }}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editStatus}
                onChange={(e: SelectChangeEvent) => setEditStatus(e.target.value as 'pending' | 'active' | 'suspended' | 'rejected')}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Venue Approval Confirmation Dialog */}
      <Dialog
        open={!!approvalDialog}
        onClose={() => {
          setApprovalDialog(null);
          setReviewNotes('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {approvalDialog?.type === 'approve' ? 'Approve' : 'Reject'} Venue Request
        </DialogTitle>
        <DialogContent>
          {approvalDialog && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {approvalDialog.request.businessName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Request Type: {approvalDialog.request.requestType === 'create' ? 'New Venue Creation' : 'Venue Claim'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Admin: {approvalDialog.request.adminName} ({approvalDialog.request.adminEmail})
              </Typography>
              <Typography variant="body2" gutterBottom>
                Address: {approvalDialog.request.address}
              </Typography>
              
              <TextField
                label="Review Notes (Optional)"
                multiline
                rows={3}
                fullWidth
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                sx={{ mt: 2 }}
                placeholder="Add any notes about this decision..."
              />
              
              {approvalDialog.type === 'approve' && approvalDialog.request.requestType === 'create' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This will create a new venue and assign it to the requesting admin.
                </Alert>
              )}
              {approvalDialog.type === 'approve' && approvalDialog.request.requestType === 'claim' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This will transfer ownership of the existing venue to the requesting admin.
                </Alert>
              )}
              {approvalDialog.type === 'reject' && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This request will be marked as rejected and the admin will be notified.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
  <Button 
    onClick={() => {
      setApprovalDialog(null);
      setReviewNotes('');
    }}
    disabled={loading} // Disable cancel during processing
  >
    Cancel
  </Button>
  <Button
    onClick={() => approvalDialog && handleVenueApproval(
      approvalDialog.request.id, 
      approvalDialog.type, 
      reviewNotes
    )}
    color={approvalDialog?.type === 'approve' ? 'success' : 'error'}
    variant="contained"
    disabled={loading} // Add this
  >
    {loading ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} color="inherit" />
        Processing...
      </Box>
    ) : (
      `${approvalDialog?.type === 'approve' ? 'Approve' : 'Reject'} Request`
    )}
  </Button>
</DialogActions>
      </Dialog>

      {/* Venue Request Details Dialog */}
      <Dialog
        open={!!selectedRequest}
        onClose={() => setSelectedRequest(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Venue Request Details</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="h6">{selectedRequest.businessName}</Typography>
                {selectedRequest.tradingName && selectedRequest.tradingName !== selectedRequest.businessName && (
                  <Typography variant="body2" color="text.secondary">
                    Trading as: {selectedRequest.tradingName}
                  </Typography>
                )}
              </Box>
              
              <Box>
                <Typography variant="subtitle2">Admin Details</Typography>
                <Typography variant="body2">Name: {selectedRequest.adminName}</Typography>
                <Typography variant="body2">Email: {selectedRequest.adminEmail}</Typography>
              </Box>
              
              <Box>
                <Typography variant="subtitle2">Business Information</Typography>
                <Typography variant="body2">Type: {selectedRequest.businessType}</Typography>
                <Typography variant="body2">Address: {selectedRequest.address}</Typography>
                {selectedRequest.venueData?.abn && (
                  <Typography variant="body2">ABN: {selectedRequest.venueData.abn}</Typography>
                )}
              </Box>
              
              <Box>
                <Typography variant="subtitle2">Contact Information</Typography>
                <Typography variant="body2">Email: {selectedRequest.venueData?.email}</Typography>
                {selectedRequest.venueData?.phone && (
                  <Typography variant="body2">Phone: {selectedRequest.venueData.phone}</Typography>
                )}
              </Box>
              
              {selectedRequest.venueData?.description && (
                <Box>
                  <Typography variant="subtitle2">Description</Typography>
                  <Typography variant="body2">{selectedRequest.venueData.description}</Typography>
                </Box>
              )}
              
              <Box>
                <Typography variant="subtitle2">Request Details</Typography>
                <Typography variant="body2">
                  Type: {selectedRequest.requestType === 'create' ? 'New Venue Creation' : 'Venue Claim'}
                </Typography>
                <Typography variant="body2">
                  Status: {selectedRequest.status?.toUpperCase() || 'PENDING'}
                </Typography>
                <Typography variant="body2">
                  Submitted: {selectedRequest.submittedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                </Typography>
              </Box>
              
              {selectedRequest.reviewNotes && (
                <Box>
                  <Typography variant="subtitle2">Review Notes</Typography>
                  <Typography variant="body2">{selectedRequest.reviewNotes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRequest(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Scrape result snackbar */}
      <Snackbar
        open={!!scrapeResult}
        autoHideDuration={6000}
        onClose={() => setScrapeResult(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setScrapeResult(null)}
          severity={scrapeResult?.message?.startsWith('Error') ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {scrapeResult?.message}
        </Alert>
      </Snackbar>

      {/* Login History Dialog — at top level so it works from any tab */}
      <Dialog
        open={!!sessionDialog}
        onClose={() => setSessionDialog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <History color="primary" />
            Login History — {sessionDialog?.admin?.name}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {sessionDialog?.admin?.email} · Last 50 sessions
          </Typography>
        </DialogTitle>
        <DialogContent>
          {sessionsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : sessionDialog?.sessions?.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <History sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No login sessions yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sessions are recorded each time this venue admin logs in.
              </Typography>
              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 1 }}>
                The next login will appear here automatically.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Browser / Device</TableCell>
                    <TableCell>Platform</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessionDialog?.sessions?.map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {session.loginAt?.toDate?.()?.toLocaleDateString('en-AU', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {session.loginAt?.toDate?.()?.toLocaleTimeString('en-AU', {
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 400, wordBreak: 'break-all', fontSize: '0.75rem' }}>
                          {session.userAgent || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={session.platform || 'portal'} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <AddVenueFromPlacesDialog
        open={addVenueDialogOpen}
        mapsLoaded={mapsLoaded}
        saving={addVenueSaving}
        existingVenues={venues}
        onClose={() => { if (!addVenueSaving) setAddVenueDialogOpen(false); }}
        onConfirm={handleCreateVenueFromPlace}
      />

    </Box>
  );
};

export default SuperAdminDashboard;